import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { OrgService, WebhookOrgJobFairInfo } from 'app/services/OrgService';
import { EmptyGuid, Roles } from "app/types";
import { Amplify, Storage } from 'aws-amplify';
import { useAuth } from "app/context/auth-context";
import { register, confirmRegistration, requestPassword } from 'app/modules/auth/redux/AuthCRUD';
import { Registration } from "../auth/components/Registration";
import { VJFPUserService } from "app/services/VJFPUserService";
import { toAbsoluteUrl } from "_metronic/helpers";
import { Alert, Button } from "react-bootstrap-v5";
import { AppModal } from "../shared/modals/AppModal";

import { JSRoutes } from "./JSPage";
import { UrlParamReplace } from "app/helpers/MiscUtils";
import useIsMounted from "app/hooks/useIsMounted";
import moment from "moment";

Amplify.configure({
    Storage: {
        AWSS3: {
            region: 'us-east-1',
            bucket: 'videojobfairs-docs'
        }
    }
});

const JobFairAuthComponent = () => {
    const { orgId, jobFairId } = useParams<{ orgId: string, jobFairId: string }>();
    const history = useHistory();

    const [email, setEmail] = useState("");
    const [userExists, setUserExists] = useState<boolean | null>(null);
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [orgDetails, setOrgDetails] = useState<WebhookOrgJobFairInfo>();
    const [userRole, setUserRole] = useState(null);
    const [showForgotPasswordAlert, setShowForgotPasswordAlert] = useState(false);
    const { user, login, logout } = useAuth();
    const isMounted = useIsMounted('JobfairAuthComponent');

    useEffect(() => {
        const fetchOrgDetails = async () => {
            if (!orgId || !jobFairId) return;
            try {
                const data = await (new OrgService().getWebhookOrgJobFairInfo(orgId, jobFairId));
                if (!isMounted()) return;
                setOrgDetails(data);
            } catch (err) {
                setError("Failed to fetch organization details.");
            }
        };

        const checkUser = async () => {
            try {
                if (!user) {
                    setUserRole(null);
                    return;
                }
                const isApplicantRole = user.roles?.includes(Roles.Applicant);
                if (isApplicantRole) {
                    history.push(UrlParamReplace(JSRoutes.Lobby));
                }
                else {
                    logout(orgId, user.id);
                }
            } catch {
                setUserRole(null);
            }
        };

        fetchOrgDetails();
        checkUser();
    }, [orgId, jobFairId, user]);

    const handleCheckUser = async () => {
        if (!email || email.trim() === "") {
            setError("Email is required.");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        setLoading(true);
        setError("");
        try {
            const vjfpUserService = new VJFPUserService();
            const response = await vjfpUserService.getUserExistsWithRole(email);
            if (response?.exists) {
                if (!response.roles?.includes(Roles.Applicant)) {
                    history.push('/auth/login?email=' + encodeURIComponent(email));
                }
                setUserExists(true);
            } else {
                setUserExists(false);
            }
        } catch (err: any) {
            setError("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async () => {
        setLoading(true);
        setError("");
        try {
            await login(email, password);
            history.push(UrlParamReplace(JSRoutes.Lobby));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div
            className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
            style={{
                backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/progress-hd.png')})`,
            }}
        >

            {userExists !== false && <div className='d-flex flex-column flex-root flex-column-fluid align-items-center justify-content-center min-vh-100'>
                <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
                    <div className='text-center mb-10'>
                        {/* begin::Logo */}
                        <a href='#'>
                            <img alt='Logo' src={toAbsoluteUrl('/media/logos/logo150.png')} className='h-45px' />
                        </a>
                    </div>

                    {orgDetails && (
                        <div className='text-center mb-10'>
                            <h1 className='text-dark mb-3'>Join {orgDetails.orgName}'s Job fair!</h1>
                            <Alert variant="info">
                                Event Date: {moment(orgDetails.jobFairEvent.startDate).format('MMMM D, YYYY')}<br />
                                Time: {moment(orgDetails.jobFairEvent.startTime, 'HH:mm:ss').format('h:mm A')} ({orgDetails.jobFairEvent.timezone})<br />
                                Duration: {orgDetails.jobFairEvent.durationHours} hours
                            </Alert>
                            <div className='text-gray-400 fw-bold fs-4'>
                                {orgDetails.sponsor?.sponsoredByText || 'Sponsored by: ' + orgDetails.sponsor?.name}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className='mb-lg-15 alert alert-danger'>
                            <div className='alert-text font-weight-bold'>{error}</div>
                        </div>
                    )}

                    {userExists === null && (
                        <div className='fv-row mb-10'>
                            <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                            <input
                                type="email"
                                className='form-control form-control-lg form-control-solid'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                                required
                            />
                            <div className='d-flex justify-content-center mt-2'>
                                <button
                                    className='btn btn-lg btn-primary m-5'
                                    onClick={handleCheckUser}
                                    disabled={loading}
                                >
                                    {!loading && <span className='indicator-label'>Continue</span>}
                                    {loading && (
                                        <span className='indicator-progress' style={{ display: 'block' }}>
                                            Please wait...
                                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {userExists === true && (
                        <div className='fv-row mb-10'>
                            <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                            <input
                                type="email"
                                className='form-control form-control-lg form-control-solid'
                                value={email}
                                readOnly={true}
                                placeholder="Email"
                                required
                            />
                            <label className='form-label fs-6 fw-bolder text-dark'>Password</label>
                            <input
                                type="password"
                                className='form-control form-control-lg form-control-solid'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                required
                            />
                            <div className='d-flex justify-content-end mt-2'>
                                <Button
                                    variant="link"
                                    onClick={() => { setShowForgotPasswordAlert(true); }}
                                    disabled={loading}
                                >
                                    Forgot Password ?
                                </Button>
                            </div>
                            <AppModal show={showForgotPasswordAlert} successText="Send verification code" onHide={() => { setShowForgotPasswordAlert(false); }} onSuccess={async () => {
                                setLoading(true);
                                await requestPassword(email);
                                setLoading(false);
                                setShowForgotPasswordAlert(false);
                                history.push(`/auth/forgot-password-code-verify/${encodeURIComponent(email)}?orgId=${orgId}&jobfairId=${jobFairId}`);
                            }} title="Forgot password?">
                                This action will send a password reset code to your email. Once received , Kindly check you email and enter the verification code in next screen.
                            </AppModal>

                            <button
                                className='btn btn-lg btn-primary w-100 mb-5'
                                onClick={handleLogin}
                                disabled={loading}
                            >
                                {!loading && <span className='indicator-label'>Login</span>}
                                {loading && (
                                    <span className='indicator-progress' style={{ display: 'block' }}>
                                        Please wait...
                                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                    </span>
                                )}
                            </button>

                        </div>
                    )}
                </div>
            </div>}
            {userExists === false && orgDetails && (
                <>
                    <div className='text-center m-5'>
                        <h1 className='text-dark mb-3'>Join {orgDetails.orgName}'s Job Fair!</h1>
                        <Alert variant="info">
                            Event Date : {orgDetails.jobFairEvent.startDate + ' ' + orgDetails.jobFairEvent.startTime} {orgDetails.jobFairEvent.timezone} ({orgDetails.jobFairEvent.durationHours}.{orgDetails.jobFairEvent.durationMinutes / 60} Hrs)
                        </Alert>    
                        <div className='text-gray-400 fw-bold fs-4'>
                            {orgDetails.sponsor?.sponsoredByText || 'Sponsored by: ' + orgDetails.sponsor?.name}
                        </div>
                    </div>
                    <Registration roles='Applicant' existingEmail={email} orgId={EmptyGuid} afterLoginHandler={() => {
                        history.push(UrlParamReplace(JSRoutes.Lobby));
                    }}></Registration>
                </>
            )}
        </div>
    );
};

export default JobFairAuthComponent;
